import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@mui/material';
import { Badge } from '@mui/material'; // Assuming Badge is part of @mui/material
import { DollarSign, Package, TrendingUp, Plus } from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend
} from 'recharts';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip } from 'recharts';
import { SalesByCategoryReportResponse } from 'src/services/apiService/response-models/report';

interface CategorySalesSummaryProps {
  categorySalesSummary: SalesByCategoryReportResponse[] | null | undefined;
}

const SalesSummary: React.FC<CategorySalesSummaryProps> = ({
  categorySalesSummary
}: CategorySalesSummaryProps) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'itemName', direction: 'asc' });

  // Calculate summary values
  const totalRevenue = categorySalesSummary.reduce(
    (sum, item) => sum + item.totalSales,
    0
  );
  const totalQuantity = categorySalesSummary.reduce(
    (sum, item) => sum + item.quantitySold,
    0
  );

  // Prepare data for bar chart
  // const itemSalesData = sampleData.map((item) => ({
  // 	name: item.itemName,
  // 	sales: item.totalSales,
  // }));

  // Prepare chart data
  const categorySalesData = categorySalesSummary.map((item) => ({
    name: (item?.categoryName || '').length > 20
      ? `${item.categoryName.substring(0, 20)}...`
      : item?.categoryName || 'Unknown',
    tooltip: item.categoryName,
    sales: item.totalSales,
    quantity: item.quantitySold
  }));



  const COLORS = [
    '#2A9D90', // Equivalent to hsl(173 58% 39%)
    '#E76E50', // Equivalent to hsl(12 76% 61%)
    '#274754', // Equivalent to hsl(197 37% 24%)
    '#E8C468', // Equivalent to hsl(43 74% 66%)
    '#F4A462' // Equivalent to hsl(27 87% 67%)
  ];

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>Category Sales Summary</h2>
      {/* Summary Cards */}
      <div
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Total Revenue</span>}
            action={<DollarSign size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              ${totalRevenue.toFixed(2)}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Total revenue from all categories
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Items Sold</span>}
            action={<Package size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              {totalQuantity}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Across {categorySalesSummary.length} different categories
            </p>
          </CardContent>
        </Card>
      </div>
      {/* Charts Section - Each chart in a single row */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Sales by Category</span>} />
          <CardContent>
            <div style={{ width: '100%', height: 500 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={categorySalesData}
                  margin={{ top: 16, right: 16, left: 0, bottom: 40 }}
          
                >
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <ChartTooltip
                    formatter={(
                      value: number,
                      name: string,
                      props: any
                    ) => {
                      const { payload } = props;
                      if (name === 'sales') {
                        return [`$${value.toFixed(2)}`, 'Sales'];
                      }
                      if (name === 'quantity') {
                        return [value, 'Quantity Sold'];
                      }
                      return [value, name];
                    }}
                    labelFormatter={(label: string, props: any) => {
                      if (props.length > 0) {
                        const { payload } = props[0];
                        return `Category: ${payload.tooltip}`;
                      }
                    }}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    itemStyle={{ color: '#333', fontSize: 12 }}
                    labelStyle={{ color: '#333', fontSize: 14 }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    formatter={(value: number, name: string, props: any   
                    ) => `$${value.toFixed(2)}`}
                  />
                  <Bar dataKey="sales" fill="#2A9D90" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Comparative Summary Table */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Comparative Category Summary</span>} />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category Name</TableCell>
                  <TableCell align="right">Total Sales</TableCell>
                  <TableCell align="right">Quantity Sold</TableCell>
                  <TableCell align="right">% of Total Sales</TableCell>
                  <TableCell align="right">% of Total Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categorySalesData.map((cat) => (
                  <TableRow key={cat.name}>
                    <TableCell>{cat.tooltip || 'Unknown'}</TableCell>
                    <TableCell align="right">${cat.sales.toFixed(2)}</TableCell>
                    <TableCell align="right">{cat.quantity}</TableCell>
                    <TableCell align="right">
                      {totalRevenue ? ((cat.sales / totalRevenue) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                    <TableCell align="right">
                      {totalQuantity ? ((cat.quantity / totalQuantity) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesSummary;
